import React, { FC, useCallback, useEffect, useState } from 'react';
import { usePermissions, useTranslate } from 'react-admin';
import { useDispatch } from 'react-redux';
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { DatePicker } from '@material-ui/pickers';
import moment, { Moment } from 'moment';
import FileSaver from 'file-saver';

import { ExportButton, Title } from '~/components';
import { NoPermissions } from '~/components/ra';
import { notification } from '~/ducks/snackbarNotifications';
import onError from '~/errorsHandler';
import time from '~/utils/time';
import {
  exportEndUsersActivity,
  getEndUsersActivity,
  EndUsersActivityReport,
} from '~/api/endUsersActivity';

const VIEW_PERMISSION = 'report.endUsersActivity.view';
const EXPORT_PERMISSION = 'report.endUsersActivity.export';
const MONTH_PARAM_FORMAT = 'YYYY-MM';

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  spacer: {
    flex: '1 1 auto',
  },
  exportButtons: {
    display: 'flex',
    marginBottom: theme.spacing(-1),
  },
  picker: {
    width: 140,
  },
  paper: {
    backgroundColor: theme.palette.common.white,
  },
  table: {
    backgroundColor: theme.palette.common.white,
  },
  metricCell: {
    fontWeight: 500,
  },
  last3MonthsRow: {
    '& td': {
      fontWeight: 700,
    },
  },
  numberCell: {
    textAlign: 'right',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(6),
  },
}));

const EndUsersActivityList: FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string, args?: any) => translate(`resources.endUsersActivity.${key}`, args);

  const { permissions } = usePermissions();

  const [from, setFrom] = useState<Moment | null>(() => moment().subtract(11, 'months'));
  const [to, setTo] = useState<Moment | null>(() => moment());
  const [report, setReport] = useState<EndUsersActivityReport | null>(null);
  const [loading, setLoading] = useState(false);

  const canExport = !!permissions?.includes(EXPORT_PERMISSION);

  const buildParams = useCallback(
    () => ({
      from: from ? from.format(MONTH_PARAM_FORMAT) : undefined,
      to: to ? to.format(MONTH_PARAM_FORMAT) : undefined,
    }),
    [from, to]
  );

  const loadReport = useCallback(() => {
    setLoading(true);
    const noticeTimer = setTimeout(() => {
      dispatch(notification({ severity: 'info', message: t('generationNotice') }));
    }, 1000);
    getEndUsersActivity(buildParams())
      .then((res) => setReport(res.data))
      .catch(onError)
      .finally(() => {
        clearTimeout(noticeTimer);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildParams, dispatch]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = (format: 'csv' | 'xlsx') => {
    exportEndUsersActivity({ ...buildParams(), format })
      .then(({ data, headers }: any) => {
        const blob = new Blob([data], { type: headers['content-type'] });
        const disposition: string | undefined = headers['content-disposition'];
        const fileName = disposition
          ? disposition.split('filename=')[1].split(';')[0].replace(/"/g, '')
          : `end-users-activity-report.${format}`;
        FileSaver.saveAs(blob, fileName);
      })
      .catch(onError);
  };

  if (!permissions) return null;
  if (!permissions.includes(VIEW_PERMISSION)) return <NoPermissions />;

  return (
    <>
      <Title
        resource='endUsersActivity'
        headline={t('title')}
        subtitle={
          report
            ? `${t('subtitle')}. ${t('generatedAt', {
                date: time(report.generatedAt, report.timeZone).format('YYYY.MM.DD HH:mm:ss'),
              })}`
            : undefined
        }
      />

      <div className={classes.toolbar}>
        <DatePicker
          className={classes.picker}
          views={['year', 'month']}
          openTo='month'
          format='YYYY.MM'
          label={t('filters.from')}
          value={from}
          maxDate={to || undefined}
          onChange={(date) => setFrom(date as Moment | null)}
          clearable
        />
        <DatePicker
          className={classes.picker}
          views={['year', 'month']}
          openTo='month'
          format='YYYY.MM'
          label={t('filters.to')}
          value={to}
          minDate={from || undefined}
          onChange={(date) => setTo(date as Moment | null)}
          clearable
        />

        <div className={classes.spacer} />

        {canExport && (
          <div className={classes.exportButtons}>
            <ExportButton
              label={t('export.csv')}
              onClick={() => handleExport('csv')}
              disabled={loading || !report}
            />
            <ExportButton
              label={t('export.excel')}
              onClick={() => handleExport('xlsx')}
              disabled={loading || !report}
            />
          </div>
        )}
      </div>

      {loading && (
        <div className={classes.centered}>
          <CircularProgress />
        </div>
      )}

      {!loading && report && (
        <Paper className={classes.paper}>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>{t('columns.period')}</TableCell>
                {report.rows.map((row) => (
                  <TableCell key={row.metric} className={classes.numberCell}>
                    {translate(`resources.endUsersActivity.metrics.${row.metric}`, {
                      _: row.metric,
                    })}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow className={classes.last3MonthsRow}>
                <TableCell className={classes.metricCell}>
                  {t('columns.last3Months', {
                    months: report.last3MonthsWindow.join(', '),
                  })}
                </TableCell>
                {report.rows.map((row) => (
                  <TableCell key={row.metric} className={classes.numberCell}>
                    {row.last3Months}
                  </TableCell>
                ))}
              </TableRow>
              {report.months.map((month) => (
                <TableRow key={month}>
                  <TableCell className={classes.metricCell}>{month}</TableCell>
                  {report.rows.map((row) => (
                    <TableCell key={row.metric} className={classes.numberCell}>
                      {row.values[month] ?? 0}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </>
  );
};

export default EndUsersActivityList;
